/** *******************************************************************************************************************
 * Copyright (c) 2020 - Fundação CERTI
 * All rights reserved.
 ******************************************************************************************************************* */

import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import Scaffold from '../../core/Scaffold/Scaffold';
import Table from '../../core/UserTable';
import {
  getByEmail, updateRole, getAllUsers, updateUser, rolesType,
} from '../../services/users';
import RolePanel from '../../core/UserTable/RolePanel';
import Warning from '../../utils/warning';
import ModalWarning from '../../core/UserTable/ModalWarning';

const roles = Object.keys(rolesType).map((roleKey) => ({
  id: roleKey,
  name: rolesType[roleKey],
  value: false,
}));

const transformRoles = (rolesToTransform) => Object.keys(rolesType).map((roleKey) => ({
  id: roleKey,
  name: rolesType[roleKey],
  value: !!rolesToTransform?.find((role) => role === roleKey),
}));

const pageTokens = [];

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState([]);
  const [page, setPage] = useState({
    number: 1,
    prevPageToken: null,
    nextPageToken: null,
    isSearching: false,
  });
  const [modalWarning, setModalWarning] = useState(false);
  const [modalRole, setModalRole] = useState(false);
  const [rolesSelected, setRolesSelected] = useState(roles);
  const [selectedOriginalRoles, setSelectedOriginalRoles] = useState(roles);

  const handleCloseModal = useCallback(() => {
    setModalRole(false);
    setModalWarning(false);
  }, [setModalRole]);

  const loadPage = useCallback(
    async (_, newPage) => {
      setLoading(true);

      const isNext = newPage > page.number;
      const { data } = await getAllUsers(isNext ? page.nextPageToken : page.prevPageToken);

      if (!data) {
        return;
      }

      setUsers(data.users);
      setTotal(data.numberOfUsers);
      setLoading(false);

      if (isNext) {
        pageTokens.push(page.prevPageToken || null);
      }

      // console.log('\t pageTokens Array <', pageTokens)
      // eslint-disable-next-line max-len
      /* console.log(`> SET PAGE\n\tprevToken: ${isNext ? page.nextPageToken : pageTokens[newPage - 1]}\n\tnextToken: ${isNext ? data.pageToken : page.prevPageToken}`)
      console.log(data.users);
      */

      setPage((prev) => ({
        number: newPage || 1,
        prevPageToken: isNext ? prev.nextPageToken : pageTokens[page.number],
        nextPageToken: isNext ? data.pageToken : prev.prevPageToken,
      }));
    },
    [page, setPage, setLoading, setUsers],
  );

  useEffect(() => {
    loadPage(null, page.number);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findByEmail = useCallback(
    (email) => {
      const getUsersByEmail = async () => {
        setLoading(true);
        const response = await getByEmail(email);

        if (!response) {
          setLoading(false);
          return;
        }

        const { users: userList, results } = response.data;

        setTotal(results);
        setUsers(userList);
        setPage((prev) => ({ ...prev, isSearching: true }));
        setLoading(false);
      };

      getUsersByEmail();
    },
    [setUsers, setTotal],
  );

  const handleChangeRoles = useCallback(
    (roles) => {
      handleCloseModal();
      const callUpdate = async () => {
        setLoading(true);

        // check difference
        const changedRoles = roles.filter(
          (item) => selectedOriginalRoles
            .find((i) => i.id === item.id)
            .value !== item.value,
        );

        // update new roles
        const newUser = await updateRole(selectedUser, changedRoles);
        setUsers((users) => {
          const dataUpdate = [...users];
          const index = selectedUser.tableData.id;
          dataUpdate[index] = newUser;
          return dataUpdate;
        });
        setLoading(false);
      };
      callUpdate();
    },
    [handleCloseModal, selectedOriginalRoles, selectedUser],
  );

  const handleShowModal = useCallback(
    (id) => {
      setModalRole(true);
      const selected = users.find((item) => item.id === id);
      setSelectedUser(selected);
      setSelectedOriginalRoles(transformRoles(selected.roles));
      setRolesSelected(transformRoles(selected.roles));
    },
    [setSelectedUser, users, setModalRole],
  );

  const onRowUpdate = useCallback(
    (newData, oldData) => new Promise((resolve) => {
      setLoading(true);

      updateUser(newData.id, {
        email: newData.email,
        professionalCouncil: newData.professionalCouncil,
      }).then((result) => {
        if (result) {
          setUsers((prev) => {
            const dataUpdate = [...prev];
            const index = oldData.tableData.id;
            dataUpdate[index] = newData;
            setLoading(false);
            return dataUpdate;
          });
          resolve();
          return;
        }
        setLoading(false);
        setModalWarning(Warning.existingEmail(newData.email));
        resolve();
      });
    }),
    [],
  );

  const usersData = useMemo(
    () => ({
      numberOfUsers: total,
      columns: [
        { title: 'Nome completo', field: 'fullname', editable: 'never' },
        { title: 'Função (papel)', field: 'roles', editable: 'never' },
        { title: 'Email', field: 'email', editable: 'onUpdate' },
        { title: 'Registro profissional', field: 'professionalCouncil', editable: 'never' },
        { title: 'Status', field: 'status', editable: 'never' },
      ],
      content: users,
    }),
    [users, total],
  );

  return (
    <Scaffold>
      <Table
        handleLoadPage={loadPage}
        onRowUpdate={onRowUpdate}
        usersData={usersData}
        page={page}
        isLoading={loading}
        findByEmail={findByEmail}
        handleShowModal={handleShowModal}
      />
      {modalRole && (
        <RolePanel
          rolesSelected={rolesSelected}
          setRolesSelected={setRolesSelected}
          onClose={handleCloseModal}
          onConfirm={handleChangeRoles}
        />
      )}
      {!!modalWarning && (
        <ModalWarning
          title={modalWarning.title}
          content={modalWarning.content}
          onClose={handleCloseModal}
        />
      )}
    </Scaffold>
  );
};

export default Users;
